import { markRaw } from 'vue'
import { useModal } from './useModal'
import type { ModalResult } from '../types/modal.types'
import type { StepDefinition } from '../types/workflow.types'

/**
 * Options for customizing the stepped modal appearance and behavior.
 */
export interface SteppedModalOptions {
  /**
   * Maximum width of the modal dialog.
   * @default 900
   */
  maxWidth?: string | number

  /** Subtitle displayed below the title in the header */
  subtitle?: string

  /**
   * Minimum height for the step content area.
   * @default '400px'
   */
  contentMinHeight?: string

  /**
   * Whether optional steps can be skipped.
   * @default true
   */
  allowSkipOptional?: boolean

  /** Pre-fill the workflow data with caller-provided answers. */
  initialData?: Record<string, unknown>
}

export interface UseSteppedModalReturn {
  openSteppedModal: <T = unknown>(
    title: string,
    steps: StepDefinition[],
    options?: SteppedModalOptions
  ) => Promise<ModalResult<T>>
}

/**
 * Composable for opening stepped modal workflows.
 *
 * Provides a pre-built modal experience for multi-step workflows with a compact
 * stepper header and navigation buttons. The modal is opened via the existing
 * modal system and returns a promise that resolves when the user completes or
 * cancels the workflow.
 *
 * @returns Object with `openSteppedModal` function
 *
 * @example Basic usage
 * ```typescript
 * const { openSteppedModal } = useSteppedModal()
 *
 * const steps: StepDefinition[] = [
 *   { id: 'config', title: 'Configure', component: markRaw(ConfigStep) },
 *   { id: 'review', title: 'Review', component: markRaw(ReviewStep) },
 * ]
 *
 * const result = await openSteppedModal('Create Item', steps)
 * if (result.confirmed) {
 *   // User completed the workflow
 *   const data = result.data as Record<string, unknown>
 *   await createItem(data.config, data.review)
 * }
 * ```
 *
 * @example With validation and options
 * ```typescript
 * const steps: StepDefinition[] = [
 *   {
 *     id: 'info',
 *     title: 'Information',
 *     component: markRaw(InfoStep),
 *     validation: (data) => !!data.info?.name || 'Name is required',
 *   },
 *   {
 *     id: 'options',
 *     title: 'Options',
 *     component: markRaw(OptionsStep),
 *     optional: true,
 *   },
 * ]
 *
 * const result = await openSteppedModal('Setup Wizard', steps, {
 *   maxWidth: 1000,
 *   subtitle: 'Complete the following steps',
 *   contentMinHeight: '500px',
 * })
 * ```
 *
 * @see {@link useSteppedWorkflow} for using the workflow logic without a modal
 * @see {@link StepDefinition} for step configuration options
 */
export function useSteppedModal(): UseSteppedModalReturn {
  const { open } = useModal()

  async function openSteppedModal<T = unknown>(
    title: string,
    steps: StepDefinition[],
    options?: SteppedModalOptions
  ): Promise<ModalResult<T>> {
    const { default: SteppedModal } = await import('../components/workflow/SteppedModal.vue')

    const modalWidth = options?.maxWidth ?? 900

    return open<T>(title, markRaw(SteppedModal), {
      width: modalWidth,
      maxWidth: modalWidth,
      hideHeader: true,
      scrollable: false,
      props: {
        steps,
        title,
        subtitle: options?.subtitle,
        contentMinHeight: options?.contentMinHeight,
        allowSkipOptional: options?.allowSkipOptional,
        initialData: options?.initialData,
      },
    })
  }

  return { openSteppedModal }
}
